/**
 * If a Button is inside a menu override it to provide a proper menu
 */
import { createElement as rc, useContext } from 'react';
import { MenuItem as _MenuItem, Text, styled, fromTheme, View, Icon, nativeOnlyStyles } from 'lib_ui-primitives';
import useEventSink from '../../hooks/useEventSink';
import useActiveRecord from '../../hooks/useActiveRecord';
import useFormContext from '../../hooks/useFormContext';
import useHiddenForValue from '../../hooks/useHiddenForValue';
import { isDisabled } from '../formButton/helpers';
import { useTheme } from 'styled-components';
import MenuContext from './MenuContext';

const MenuText = styled(Text)`
    ${fromTheme('menu', 'menuItem', 'defaultFont')}
`;

const LeftToRight = styled(View)`
    flex-direction: row;
    gap: ${fromTheme('menu', 'menuItem', 'iconTextGap')};
`;

let StyledMenuItem = styled(_MenuItem).attrs({ name: 'menu-item' })``;
StyledMenuItem = nativeOnlyStyles(StyledMenuItem)`
    padding-left: ${fromTheme('menu', 'native', 'paddingLeft')};
`;

// Styled View component for embedded version with same visual styling
let StyledMenuItemView = styled(View).attrs({ name: 'menu-item-embedded' })``;
StyledMenuItemView = nativeOnlyStyles(StyledMenuItemView)`
    padding-left: ${fromTheme('menu', 'native', 'paddingLeft')};
`;

export function MenuItem(props) {
    const { hNode = {}, actionPayload = {}, showIcons = true, onSelect, ...otherProps } = props;

    const {
        forAction,
        formAction: _formAction,
        title,
        disabled: hNodeDisabled,
        namespace: hNamespace,
        relation: hRelation,
        type: hType,
        subtype,
        iconName,
        withSelection,
        useEmbeddedVersion = false
    } = hNode;

    const action = forAction || (_formAction === 'print' ? 'submit' : _formAction);

    const theme = useTheme();
    const color = theme.menu.color;
    const formContext = useFormContext();
    const hidden = useHiddenForValue(hNode);
    const activeRecord = useActiveRecord() || {};

    // Get namespace/relation from MenuContext (passed from parent MenuButton via SubMenu)
    // Falls back to activeRecord only if not in a menu context
    const menuContext = useContext(MenuContext);
    const menuActionPayload = menuContext?.actionPayload;

    const namespace = hNamespace || menuActionPayload?.namespace || activeRecord.namespace;
    const relation = hRelation || menuActionPayload?.relation || activeRecord.relation;
    const { record, type } = activeRecord;

    const disabled = hNodeDisabled || (formContext && isDisabled(formContext, action));

    const [, publish] = useEventSink();
    function onClick(e) {
        if (onSelect) {
            onSelect(e);
        }

        const finalRecord = actionPayload?.record || record;
        const payload = { ...actionPayload, record: finalRecord };

        // 'open' actions open dialogs and shouldn't go through selection/bulk flow
        // even if parent MenuButton has withSelection enabled
        const shouldUseSelection = withSelection && action !== 'open';

        if (shouldUseSelection) {
            publish(payload, {
                verb: 'selection',
                namespace,
                relation,
                type: action,
                subtype,
                routePath: props.currentRoute
            });
        } else {
            publish(payload, {
                verb: action,
                namespace,
                relation,
                type: subtype || hType || type,
                routePath: props.currentRoute
            });
        }
    }

    if (hidden) return null;

    // Choose component based on useEmbeddedVersion
    const MenuItemComponent = useEmbeddedVersion ? StyledMenuItemView : StyledMenuItem;

    return rc(
        MenuItemComponent,
        { onClick, disabled, hNode, ...otherProps },
        rc(
            LeftToRight,
            null,
            showIcons && iconName && rc(Icon, { name: iconName, size: 'md', children: iconName, color }),
            rc(MenuText, null, title)
        )
    );
}

export default MenuItem;
